import { useMemo, useState } from "react";

import { subMonths } from "date-fns";
import stringify from "query-string";
import { generatePath } from "react-router-dom";

import { postTrackingData } from "@/api/endpoints/track";
import ArrowTopRightOnSquareIcon from "@/assets/icons/arrow-top-right-on-square.svg";
import ChartBarIcon from "@/assets/icons/chart-bar.svg";
import ChatBubbleIcon from "@/assets/icons/chat-bubble.svg";
import { SEARCH_KIND } from "@/constants";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { FEATURE } from "@/features";
import { useUser } from "@/hooks/useUser";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { SearchFiltersFull } from "@/types/SearchFilter";
import { copyToClipboard, formatDateForApi, slugify } from "@/utils";
import AlertShareExportCard from "@components/AlertShareExportCard";
import Button from "@components/Button";
import { Feature } from "@components/Feature";
import { Checkbox } from "@components/Field";
import Modal, {
  ModalContent,
  ModalDescription,
  ModalTitle,
} from "@components/Modal";
import { ModalProps } from "@components/Modal/Modal";

import { Json, Rss } from "./partials/links";
import { useAppliedAlertParams } from "./partials/useAppliedAlertParams";

import styles from "./AlertShareModal.module.css";

const AlertShareModal = ({
  filters,
  ...modalProps
}: ModalProps & {
  filters: Partial<SearchFiltersFull>;
}) => {
  const { alert } = useMentionCards();
  const { data } = useUser();
  const { danger } = useMessages();
  const stringifiedFilters = useAppliedAlertParams();

  const [copiedShareUrl, setCopiedShareUrl] = useState(false);
  const [copiedInsightsShareUrl, setCopiedInsightsShareUrl] = useState(false);
  const [shouldShowKpis, setshouldShowKpis] = useState(true);

  const shareUrl = useMemo(() => {
    if (!data?.ok) return null;
    const user = data.data;

    const isSaved = alert?.kind === SEARCH_KIND.SAVED;
    const isReport = alert?.kind === SEARCH_KIND.REPORT;

    if (!isSaved && !isReport) {
      return null;
    }

    const baseUrl = `${window.location.origin}${generatePath(
      ROUTES.ALERT_PUBLIC,
      {
        uuid: alert?.uuid,
        slug: slugify(alert?.title),
        language: user.language,
      }
    )}?${stringifiedFilters}`;

    return stringify.stringifyUrl({
      url: baseUrl,
      query: {
        kpis: shouldShowKpis,
        sentiment: true,
      },
    });
  }, [
    alert?.kind,
    alert?.title,
    alert?.uuid,
    shouldShowKpis,
    stringifiedFilters,
    data,
  ]);

  const insightsShareUrl = alert?.insights_report_uuid
    ? stringify.stringifyUrl(
        {
          url: `${window.location.origin}${generatePath(
            ROUTES.MENTION_INSIGHTS_SHARE,
            {
              report_uuid: alert.insights_report_uuid,
            }
          )}`,
          query: {
            ...filters,
            start_date:
              filters.start_date || formatDateForApi(subMonths(new Date(), 3)),
            end_date: filters.end_date || formatDateForApi(new Date()),
          },
        },
        { arrayFormat: "comma" }
      )
    : undefined;

  const handleClipboardCopyShareUrl = async () => {
    postTrackingData(EVENT.SHARE_ALERT_COPIED, {
      shareUrl: shareUrl,
      uuid: alert?.uuid,
    });
    try {
      if (shareUrl) await copyToClipboard(shareUrl);
      setCopiedShareUrl(true);
    } catch {
      danger({
        title: L.MENTIONS_SHARE_COPY_TO_CLIPBOARD_FAILED,
      });
    }
  };

  const handleClipboardCopyInsightsShareUrl = async () => {
    postTrackingData(EVENT.SHARE_ALERT_ANALYTICS_COPIED, {
      shareUrl: insightsShareUrl,
      uuid: alert?.uuid,
    });
    try {
      if (insightsShareUrl) await copyToClipboard(insightsShareUrl);
      setCopiedInsightsShareUrl(true);
    } catch {
      danger({
        title: L.MENTIONS_SHARE_COPY_TO_CLIPBOARD_FAILED,
      });
    }
  };

  if (!alert) return null;

  return (
    <Modal {...modalProps}>
      <ModalTitle>
        {L.SHARE_ALERT_MODAL_TITLE.replaceAll("{}", alert.title)}
      </ModalTitle>

      <ModalDescription>{L.SHARE_ALERT_MODAL_LEDE}</ModalDescription>
      <ModalContent>
        <div className={styles.cards}>
          {shareUrl && (
            <AlertShareExportCard.Root>
              <AlertShareExportCard.Left>
                <AlertShareExportCard.Icon icon={ChatBubbleIcon} />
                <AlertShareExportCard.LeftText>
                  <AlertShareExportCard.Title>
                    {L.SHARE_ALERT_MODAL_MENTIONS}
                  </AlertShareExportCard.Title>

                  <Checkbox
                    label={L.SHARE_WITH_KPIS}
                    checked={shouldShowKpis}
                    onChange={(e) => setshouldShowKpis(e.target.checked)}
                  />
                </AlertShareExportCard.LeftText>
              </AlertShareExportCard.Left>

              <AlertShareExportCard.Right>
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-mentions-link-preview"
                  onClick={() =>
                    postTrackingData(EVENT.SHARE_ALERT_CLICKED, {
                      shareUrl,
                      uuid: alert?.uuid,
                    })
                  }
                >
                  <ArrowTopRightOnSquareIcon className={styles.arrow} />
                </a>

                <Button
                  onClick={handleClipboardCopyShareUrl}
                  className="ml-3"
                  size="md"
                >
                  {copiedShareUrl
                    ? L.SHARE_MODAL_COPY_URL_SUCCESS
                    : L.SHARE_MODAL_COPY_URL}
                </Button>
              </AlertShareExportCard.Right>
            </AlertShareExportCard.Root>
          )}

          <AlertShareExportCard.Root>
            <AlertShareExportCard.Left>
              <AlertShareExportCard.Icon color="blue" icon={ChartBarIcon} />
              <AlertShareExportCard.LeftText>
                <AlertShareExportCard.Title>
                  {L.SHARE_ALERT_MODAL_ANALYTICS}
                </AlertShareExportCard.Title>
              </AlertShareExportCard.LeftText>
            </AlertShareExportCard.Left>

            <AlertShareExportCard.Right>
              <a
                href={insightsShareUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="share-analytics-link-preview"
                onClick={() =>
                  postTrackingData(EVENT.SHARE_ALERT_ANALYTICS_COPIED, {
                    shareUrl: insightsShareUrl,
                    uuid: alert?.uuid,
                  })
                }
              >
                <ArrowTopRightOnSquareIcon className={styles.arrow} />
              </a>

              <Button
                variant="secondary"
                onClick={handleClipboardCopyInsightsShareUrl}
                className="ml-3"
                size="md"
              >
                {copiedInsightsShareUrl
                  ? L.SHARE_MODAL_COPY_URL_SUCCESS
                  : L.SHARE_MODAL_COPY_URL}
              </Button>
            </AlertShareExportCard.Right>
          </AlertShareExportCard.Root>
        </div>

        <div className={styles.links}>
          <Feature required={FEATURE.HAS_RSS_FEATURE}>
            <Rss
              onClick={() => postTrackingData(EVENT.SHARE_ALERT_CLICKED_RSS)}
            />
          </Feature>
          <Json
            onClick={() => postTrackingData(EVENT.SHARE_ALERT_CLICKED_API)}
          />
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AlertShareModal;
